import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { selectUser } from '../../../../../store/app.selectors';
import { SnackBarService } from '../../../../../core/services/snackbar.service';
import { IUserProfile } from '../../../../user/interfaces/IUserProfile';
import { ChannelRoles, ChannelType, IChannelCreationDTO } from '../../../interfaces/IChannel';
import { IMessageableEntity } from '../../../interfaces/IMessageableEntity';
import { ITag } from '../../../interfaces/ITag';
import { MessagingApiService } from '../../../services/messaging-api/messaging-api.service';
import { selectChannel, setNewChannel, toggleSidebar } from '../../../store/chat.actions';
import { AppStateWithChat } from '../../../store/chat.reducer';

@Component({
  selector: 'ngx-new-broadcast-channel',
  templateUrl: './new-broadcast-channel.component.html',
  styleUrls: ['./new-broadcast-channel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NewBroadcastChannelComponent {
  private fb = inject(FormBuilder);
  private store = inject(Store<AppStateWithChat>);
  private dialogRef = inject(MatDialogRef<NewBroadcastChannelComponent>);
  private dataService = inject(MessagingApiService);
  private snackBarService = inject(SnackBarService);

  user = toSignal(this.store.select(selectUser), { initialValue: null as IUserProfile | null });
  usersSelected: IMessageableEntity[] = [];
  tagsSelected: ITag[] = [];
  isLoading = signal(false);
  isLoadingTagsCheck = signal(false);
  maxChars = 250;
  newChatForm: FormGroup = this.fb.group({
    displayName: [null, [Validators.required]],
  });

  close(): void {
    this.dialogRef.close();
  }

  createChannel(): void {
    const user = this.user();
    if (!user) {
      return;
    }
    this.isLoading.set(true);
    this.store.dispatch(toggleSidebar({ active: false }));
    const channel: IChannelCreationDTO = {
      displayName: this.newChatForm.value.displayName as string,
      type: ChannelType.BROADCAST,
      isPrivate: true,
      role: ChannelRoles.ADMIN,
    };
    this.dataService
      .createChannel(channel, [...this.usersSelected.map((e) => e.uid), user.subjectMessageableEntity.uid])
      .subscribe({
        next: (newChannel) => {
          this.store.dispatch(setNewChannel({ channel: newChannel }));
          this.store.dispatch(selectChannel({ channel: newChannel }));
          this.close();
        },
        error: () => {
          this.isLoading.set(false);
          this.snackBarService.showErrorSnackBar();
        },
      });
  }

  selectAllUsersByTag(): void {
    this.usersSelected = [];
    if (this.tagsSelected.length) {
      this.isLoadingTagsCheck.set(true);
      this.dataService
        .searchUsers({ searchTerm: '', page: 1, size: 999, tags: this.tagsSelected, useGroup: true })
        .subscribe({
          next: (data) => {
            this.usersSelected = data;
            this.isLoadingTagsCheck.set(false);
          },
          error: () => {
            this.isLoadingTagsCheck.set(false);
          },
        });
    }
  }

  tagsChanged(tags: ITag[]): void {
    this.tagsSelected = tags;
    this.selectAllUsersByTag();
  }
}
